package sarka.design.skoria.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import sarka.design.skoria.isAndroidPlatform
import sarka.design.skoria.model.ChatStatus
import sarka.design.skoria.model.ChatUiState
import sarka.design.skoria.presentation.ChatController
import sarka.design.skoria.ui.composables.AndroidCore
import sarka.design.skoria.ui.composables.WebDesktopCore
import sarka.design.skoria.ui.theme.RCTheme
import skoria.composeapp.generated.resources.Res
import skoria.composeapp.generated.resources.skoria_logo

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatScreen(
    controller: ChatController,
    state: ChatUiState,
    onInputChange: (String) -> Unit,
    onSend: () -> Unit,
    onSkip: () -> Unit,
    onBack: () -> Unit,
    onReconnect: () -> Unit,
) {
    val local by controller.localVideoTrack.collectAsState(initial = null)
    val remote by controller.remoteVideoTrack.collectAsState(initial = null)

    ChatScreenView(
        state = state,
        local = local,
        remote = remote,
        onInputChange = onInputChange,
        onSend = onSend,
        onSkip = onSkip,
        onBack = onBack,
        onReconnect = onReconnect,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatScreenView(
    state: ChatUiState,
    local: sarka.design.skoria.webrtc.PlatformVideoTrack? = null,
    remote: sarka.design.skoria.webrtc.PlatformVideoTrack? = null,
    onInputChange: (String) -> Unit,
    onSend: () -> Unit,
    onSkip: () -> Unit,
    onBack: () -> Unit,
    onReconnect: () -> Unit,
    isPreview: Boolean = false,
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Image(
                            painter = painterResource(Res.drawable.skoria_logo),
                            contentDescription = "Skoria Logo",
                            modifier = Modifier.size(32.dp)
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = "Skoria",
                            color = RCTheme.colors.primary,
                            fontSize = 28.sp
                        )
                        Spacer(Modifier.width(16.dp))
                        Text(titleFor(state.status))
                    }
                },
                navigationIcon = {
                    if (isAndroidPlatform) {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    }
                },
                actions = {
                    when (state.status) {
                        ChatStatus.CONNECTED, ChatStatus.WAITING_FOR_PARTNER ->
                            IconButton(
                                onClick = {},
                                enabled = true,
                                colors = IconButtonDefaults.iconButtonColors(
                                    contentColor = RCTheme.colors.onSurface,
                                    disabledContentColor = RCTheme.colors.onSurface.copy(alpha = 0.38f)
                                )
                            ) {
                                Icon(
                                    modifier = Modifier.size(48.dp),
                                    imageVector = Icons.Default.Menu,
                                    contentDescription = "Menu",
                                )
                            }
                        else ->
                            TextButton(onClick = onReconnect) { Text("Reconnect") }
                    }
                }
            )
        },
        bottomBar = {
            Row(
                modifier = Modifier
                    .imePadding()
                    .windowInsetsPadding(WindowInsets.navigationBars)
                    .fillMaxWidth()
                    .padding(8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                TextField(
                    value = state.input,
                    onValueChange = onInputChange,
                    modifier = Modifier.weight(1f)
                        .onPreviewKeyEvent { e ->
                            val isEnter = e.key == Key.Enter || e.key == Key.NumPadEnter
                            if (e.type == KeyEventType.KeyDown && isEnter) {
                                if (state.status == ChatStatus.CONNECTED && state.input.isNotBlank()) {
                                    onSend()
                                }
                                true // consume
                            } else {
                                false
                            }
                        },
                    placeholder = { Text("Type...") },
                    singleLine = true,
                )

                IconButton(
                    onClick = onSend,
                    enabled = state.status == ChatStatus.CONNECTED && state.input.isNotBlank(),
                    colors = IconButtonDefaults.iconButtonColors(
                        contentColor = RCTheme.colors.onSurface,
                        disabledContentColor = RCTheme.colors.onSurface.copy(alpha = 0.38f)
                    )
                ) {
                    Icon(
                        modifier = Modifier.size(48.dp),
                        imageVector = Icons.AutoMirrored.Default.Send,
                        contentDescription = "Send",
                    )
                }
            }
        }
    ) { padding ->
        if (isPreview) {
            // Skip video rendering in preview — EGL/WebRTC not available
            Box(
                modifier = Modifier.padding(padding).fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Text("Video area (preview)")
            }
        } else if (isAndroidPlatform) {
            AndroidCore(
                padding = padding,
                local = local,
                remote = remote,
                onSkip = onSkip,
                state = state
            )
        } else {
            WebDesktopCore(
                padding = padding,
                local = local,
                remote = remote,
                onSkip = onSkip,
                state = state
            )
        }
    }
}

private fun titleFor(status: ChatStatus) = when (status) {
    ChatStatus.CONNECTING -> "Connecting to server…"
    ChatStatus.WAITING_FOR_PARTNER -> "Looking for partner…"
    ChatStatus.CONNECTED -> "Connected to user"
    ChatStatus.DISCONNECTED -> "Disconnected"
    ChatStatus.ERROR -> "Error"
}

@Composable
@Preview
private fun ChatScreenPreview() {
    RCTheme {
        ChatScreenView(
            state = ChatUiState(
                status = ChatStatus.CONNECTED,
                messages = listOf(
                    sarka.design.skoria.model.ChatMessage(fromMe = true, text = "Hey!"),
                    sarka.design.skoria.model.ChatMessage(fromMe = false, text = "Hello there"),
                    sarka.design.skoria.model.ChatMessage(fromMe = true, text = "How are you?"),
                ),
                input = ""
            ),
            onInputChange = {},
            onSend = {},
            onSkip = {},
            onBack = {},
            onReconnect = {},
            isPreview = true,
        )
    }
}
